import React from 'react';
import {Box, Button, Stack} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import {
  ConditionGroup as ConditionGroupModel,
  ConditionNode,
  duplicateConditionGroupIds,
} from '../../headerAnalysisRuleModel';
import {ConditionGroup} from './ConditionGroup';
import {OperatorSelector} from './OperatorSelector';

interface ConditionBuilderProps {
  group: ConditionGroupModel;
  conditionErrors: Record<string, string>;
  highlightDuplicateGroups: boolean;
  onOperator: (groupId: string, operator: 'all' | 'any') => void;
  onAddGroupCondition: (groupId: string) => void;
  onAddGroup: () => void;
  onDetail: (condition: ConditionNode) => void;
  onRemove: (conditionId: string) => void;
}

export const ConditionBuilder: React.FC<ConditionBuilderProps> = ({
  group, conditionErrors, highlightDuplicateGroups, onOperator, onAddGroupCondition, onAddGroup,
  onDetail, onRemove,
}) => {
  const hasMultipleGroups = group.children.length > 1;
  const duplicateGroupIds = highlightDuplicateGroups
    ? duplicateConditionGroupIds(group)
    : new Set<string>();

  return <Box data-testid="condition-builder">
    <Box
      className="trigger-conditions-root"
      data-testid="condition-group-1"
      data-root-bracket={hasMultipleGroups ? 'visible' : 'hidden'}
      sx={{
        position: 'relative',
        ...(hasMultipleGroups ? {
          pl: 2,
          pb: 1,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: '8px',
            boxSizing: 'border-box',
            borderLeft: group.operator === 'any' ? '2px dashed #ff8d35' : '1px solid #3a618e',
            borderRadius: '8px 0 0 8px',
            pointerEvents: 'none',
          },
        } : {}),
      }}
    >
      {hasMultipleGroups && (
        <OperatorSelector
          groupId={group.id}
          operator={group.operator}
          accessibleLabel="Root operator"
          onChange={onOperator}
        />
      )}
      <Stack spacing={1.5}>
        {group.children.map((child, rootIndex) => (
          <ConditionGroup
            key={child.id}
            group={child}
            conditionErrors={conditionErrors}
            isDuplicate={duplicateGroupIds.has(child.id)}
            rootPosition={rootIndex + 1}
            simpleNumbering={!hasMultipleGroups}
            onOperator={onOperator}
            onAdd={onAddGroupCondition}
            onDetail={onDetail}
            onRemove={onRemove}
          />
        ))}
      </Stack>
      <Button startIcon={<AddIcon/>} onClick={onAddGroup} sx={{mt: 1}}>
        Add Group
      </Button>
    </Box>
  </Box>;
};
